// src/contexts/YearProvider.js
import React, { useState, useEffect, useContext } from "react";
import { YearContext } from "./YearContext"; // Import YearContext
import { LeagueContext } from "./LeagueContext";
import { 
  getCurrentSeasonInfo, 
  isSeasonActive, 
  getSeasonYearInfo,
  getSeasonStatusText 
} from "../services/seasonService";

export function YearProvider({ children }) {
  const { currentLeague, leagueDataVersion } = useContext(LeagueContext);
  const [seasonYear, setSeasonYear] = useState(null);
  const [nextYear, setNextYear] = useState(null);
  const [submitDate, setSubmitDate] = useState(null);
  const [seasonInfo, setSeasonInfo] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [seasonStatus, setSeasonStatus] = useState('Loading...');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSeasonData = async () => {
      try {
        // Don't set loading to true if we're just updating the deadline
        if (!seasonYear) {
          setLoading(true);
        }
        
        // Get current season info from Firestore (populated by Cloud Functions)
        const currentSeasonInfo = await getCurrentSeasonInfo();
        
        if (currentSeasonInfo) {
          // Use API-sourced season data
          setSeasonInfo(currentSeasonInfo);
          setSeasonYear(currentSeasonInfo.year);
          setNextYear(currentSeasonInfo.nextYear);
          
          // Set submit date - prioritize league deadline, fallback to season prediction deadline, then season start
          // Debug logging to track deadline calculation
          if (process.env.NODE_ENV === 'development') {
            console.log('🗓️ YearProvider: Calculating deadline for league:', currentLeague?.name || 'No league', {
              leagueDeadline: currentLeague?.settings?.predictionDeadline,
              seasonDeadline: currentSeasonInfo.predictionDeadline,
              seasonStart: currentSeasonInfo.startDate
            });
          }
          
          let submit;
          const seasonStart = new Date(currentSeasonInfo.startDate);
          const seasonEnd = new Date(currentSeasonInfo.endDate);
          // Season-level deadline (auto-populated from the API each season), falling back
          // to the season start date.
          const seasonDeadline = currentSeasonInfo.predictionDeadline
            ? new Date(currentSeasonInfo.predictionDeadline)
            : seasonStart;

          if (currentLeague && currentLeague.settings && currentLeague.settings.predictionDeadline) {
            // Use the league-specific deadline only if it falls within the CURRENT season.
            // A deadline outside [seasonStart, seasonEnd] is stale (frozen from a prior
            // season at league-creation time), so fall back to the season deadline. This
            // keeps existing leagues self-correcting each season with no manual editing.
            submit = new Date(currentLeague.settings.predictionDeadline);
            if (submit > seasonEnd || submit < seasonStart) {
              submit = seasonDeadline;
            }
          } else {
            submit = seasonDeadline;
          }
          
          // Debug logging to show final deadline
          if (process.env.NODE_ENV === 'development') {
            console.log('🎯 YearProvider: Final deadline set to:', submit.toLocaleDateString(), submit.toISOString());
          }
          
          setSubmitDate(submit);
          
          // Check if season is active
          const active = await isSeasonActive(currentSeasonInfo);
          setIsActive(active);
          
          // Get season status text
          const statusText = await getSeasonStatusText(currentSeasonInfo);
          setSeasonStatus(statusText);
        } else {
          // Fallback to original logic if no season data available
          const yearInfo = await getSeasonYearInfo();
          setSeasonYear(yearInfo.seasonYear);
          setNextYear(yearInfo.nextYear);
          
          const submit = new Date(`${yearInfo.seasonYear}-08-30`);
          setSubmitDate(submit);
          setIsActive(false);
          setSeasonStatus('Season data unavailable');
        }
      } catch (error) {
        console.error('Error loading season data:', error);
        
        // Emergency fallback to original logic
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        let season;
        if (currentMonth >= 1 && currentMonth <= 6) {
          season = currentYear - 1;
        } else {
          season = currentYear;
        }

        const next = season + 1;
        const submit = new Date(`${season}-08-30`);

        setSeasonYear(season);
        setNextYear(next);
        setSubmitDate(submit);
        setIsActive(false);
        setSeasonStatus('Error loading season data');
      } finally {
        setLoading(false);
      }
    };

    loadSeasonData();
    // seasonYear is intentionally omitted: it is set inside this effect and only
    // gates the loading spinner, so depending on it would trigger a redundant reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeague, leagueDataVersion]); // Re-run when league changes or data version increments

  return (
    <YearContext.Provider value={{ 
      seasonYear, 
      nextYear, 
      submitDate, 
      seasonInfo,
      isSeasonActive: isActive,
      seasonStatus,
      loading,
      currentLeague,
      isLeagueDeadline: !!(currentLeague && currentLeague.settings && currentLeague.settings.predictionDeadline)
    }}>
      {children}
    </YearContext.Provider>
  );
}
